import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Printer, RefreshCw, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";

const fmt = (iso) =>
  new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" });

/** Day-of master Run Sheet — admins + designated DA Staff, read-only. */
export default function RunSheet() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [view, setView] = useState("time"); // time | table
  const [tableFilter, setTableFilter] = useState("all");
  const [q, setQ] = useState("");
  const [now, setNow] = useState(Date.now());

  const load = () => {
    api.get("/matchmaking/runsheet")
      .then(({ data }) => { setData(data); setErr(""); })
      .catch((e) => setErr(e?.response?.status === 403
        ? "The Run Sheet is limited to Display America staff."
        : "Please sign in first (DA Staff use the Attendee tab with the event access code)."));
  };
  useEffect(() => { load(); const t = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(t); }, []);

  const meetings = useMemo(() => {
    let list = data?.meetings || [];
    if (tableFilter === "stations") list = list.filter((m) => !m.table_number);
    else if (tableFilter !== "all") list = list.filter((m) => String(m.table_number) === tableFilter);
    if (q) {
      const t = q.toLowerCase();
      list = list.filter((m) => (m.party_a + " " + m.party_b + " " + m.location).toLowerCase().includes(t));
    }
    return list;
  }, [data, tableFilter, q]);

  const blocks = useMemo(() => {
    const map = new Map();
    for (const m of meetings) {
      const key = `${m.start_iso}|${m.end_iso}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(m);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [meetings]);

  const isNow = (key) => {
    const [s, e] = key.split("|");
    return s && e && now >= new Date(s).getTime() && now < new Date(e).getTime();
  };

  const unusedTables = (key, rows) => {
    const [s] = key.split("|");
    if (!(data?.rounds || []).some((r) => r.start_iso === s)) return [];
    const used = new Set(rows.filter((m) => m.table_number).map((m) => m.table_number));
    return Array.from({ length: data?.tables || 8 }, (_, i) => i + 1).filter((t) => !used.has(t));
  };

  const tableGroups = useMemo(() => {
    const groups = [];
    for (let t = 1; t <= (data?.tables || 8); t++) {
      groups.push({ label: `Table M-${t}`, rows: meetings.filter((m) => m.table_number === t) });
    }
    groups.push({ label: "Showcase Stations & Other", rows: meetings.filter((m) => !m.table_number) });
    return groups.filter((g) => g.rows.length > 0);
  }, [meetings, data]);

  return (
    <div className="min-h-screen bg-white text-black" data-testid="runsheet-page">
      <style>{`@media print { .no-print { display: none !important; } body { background: #fff; } }`}</style>

      <div className="no-print border-b border-zinc-200 px-6 py-4 flex flex-wrap items-center gap-3 sticky top-0 bg-white z-10">
        <Link to="/admin/matchmaking" className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-black">
          <ArrowLeft size={14} /> Console
        </Link>
        <div className="flex border border-zinc-300">
          {["time", "table"].map((v) => (
            <button key={v} onClick={() => setView(v)}
              className={`px-4 h-9 text-[11px] uppercase tracking-[0.16em] ${view === v ? "bg-black text-white" : ""}`}
              data-testid={`runsheet-view-${v}`}>
              By {v}
            </button>
          ))}
        </div>
        <select value={tableFilter} onChange={(e) => setTableFilter(e.target.value)}
          className="h-9 border border-zinc-300 px-2 text-sm" data-testid="runsheet-table-filter">
          <option value="all">All locations</option>
          {Array.from({ length: data?.tables || 8 }, (_, i) => i + 1).map((t) => (
            <option key={t} value={String(t)}>Table M-{t}</option>
          ))}
          <option value="stations">Showcase Stations</option>
        </select>
        <Input placeholder="Search company or rep…" value={q} onChange={(e) => setQ(e.target.value)}
          className="h-9 rounded-none w-56" data-testid="runsheet-search" />
        <div className="ml-auto flex items-center gap-3">
          {data && (
            <span className="text-[11px] text-zinc-500" data-testid="runsheet-updated">
              Last updated {new Date(data.generated_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" })} ET · read-only
            </span>
          )}
          <button onClick={load} className="h-9 px-3 border border-zinc-300 hover:border-black inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em]" data-testid="runsheet-refresh">
            <RefreshCw size={12} /> Refresh
          </button>
          <button onClick={() => window.print()} className="h-9 px-4 bg-black text-white inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em]" data-testid="runsheet-print">
            <Printer size={12} /> Print
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="text-[10px] uppercase tracking-[0.3em]">Business Matchmaking · Day-Of Run Sheet</div>
        <h1 style={{ fontFamily: "Georgia, serif" }} className="text-2xl mt-2">Travel + Leisure Strategic Supplier Summit — Friday, September 11, 2026</h1>

        {err && (
          <p className="mt-8 text-sm" data-testid="runsheet-error">
            {err} <Link to="/login" className="underline">Sign in</Link>
          </p>
        )}
        {data && meetings.length === 0 && (
          <p className="mt-8 text-sm text-zinc-500">No meetings match the current filters.</p>
        )}

        {data && view === "time" && blocks.map(([key, rows]) => (
          <div key={key} className={`mt-6 ${isNow(key) ? "border-2 border-black p-3" : ""}`} data-testid={`runsheet-block-${key.slice(11, 16)}`}>
            <div className="flex items-baseline gap-3 border-b-2 border-black pb-1">
              <span className="font-semibold tabular-nums">{fmt(key.split("|")[0])} – {fmt(key.split("|")[1])} ET</span>
              {isNow(key) && <span className="text-[10px] uppercase tracking-[0.2em] bg-black text-white px-2 py-[2px]" data-testid="runsheet-now">Now</span>}
            </div>
            <table className="w-full text-sm mt-1">
              <tbody>
                {rows.map((m) => (
                  <tr key={m.id} className="border-b border-zinc-200 align-top">
                    <td className="py-1.5 pr-3 w-[2.2in] font-medium tabular-nums">{m.location}</td>
                    <td className="py-1.5 pr-3">{m.party_a} <span className="text-zinc-400">×</span> {m.party_b}</td>
                    <td className="py-1.5 text-[10px] uppercase tracking-wide text-right w-[1.2in]">{m.kind}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {unusedTables(key, rows).length > 0 && (
              <div className="mt-1 text-[11px] text-zinc-400">
                Unused: {unusedTables(key, rows).map((t) => `M-${t}`).join(" · ")}
              </div>
            )}
          </div>
        ))}

        {data && view === "table" && tableGroups.map((g) => (
          <div key={g.label} className="mt-6">
            <div className="font-semibold border-b-2 border-black pb-1">{g.label}</div>
            <table className="w-full text-sm mt-1">
              <tbody>
                {g.rows.map((m) => (
                  <tr key={m.id} className="border-b border-zinc-200 align-top">
                    <td className="py-1.5 pr-3 w-[1.8in] tabular-nums">{m.start_iso ? `${fmt(m.start_iso)} – ${fmt(m.end_iso)}` : "—"}</td>
                    <td className="py-1.5 pr-3">{m.party_a} <span className="text-zinc-400">×</span> {m.party_b}</td>
                    <td className="py-1.5 text-[10px] uppercase tracking-wide text-right w-[1.2in]">{m.kind}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        <div className="mt-10 pt-3 border-t border-zinc-300 text-[11px] text-zinc-500">
          Internal — Display America staff only. Participants check in at the Business Matchmaking Check-In Table inside the Supplier Showcase at least five minutes before each meeting; back-to-back meetings check in once before the first meeting, then follow staff direction.
        </div>
      </div>
    </div>
  );
}
